/**
 * NewsletterUnsubscribed.js
 * Friendly unsubscribe confirmation page shown after clicking the email link.
 * URL: /newsletter/unsubscribed?status=success|already|invalid
 */
import React from 'react';
import { Box, Typography, Container, Button, Stack } from '@mui/material';
import { CheckCircle as CheckIcon, Info as InfoIcon, Error as ErrorIcon } from '@mui/icons-material';

export default function NewsletterUnsubscribed() {
    const status = new URLSearchParams(window.location.search).get('status') || 'success';

    const STATUS_CONFIG = {
        success: {
            icon:    <CheckIcon sx={{ fontSize: 64, color: '#10d98a', mb: 2 }} />,
            heading: "You've been unsubscribed",
            body:    "We've removed your email from all Socialixer marketing communications. You won't hear from us again.",
            color:   '#10d98a',
        },
        already: {
            icon:    <InfoIcon sx={{ fontSize: 64, color: '#6366f1', mb: 2 }} />,
            heading: 'Already unsubscribed',
            body:    'Your email is no longer on our list. This link may have already been used.',
            color:   '#6366f1',
        },
        invalid: {
            icon:    <ErrorIcon sx={{ fontSize: 64, color: '#ef4444', mb: 2 }} />,
            heading: 'Invalid link',
            body:    'This unsubscribe link is invalid or has expired. Please contact us if you need help.',
            color:   '#ef4444',
        },
    };
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.success;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#0a0a14', display: 'flex', alignItems: 'center', py: 8 }}>
            <Container maxWidth="sm">
                <Box sx={{
                    p: { xs: 4, md: 6 }, borderRadius: '24px', textAlign: 'center',
                    bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)',
                    backdropFilter: 'blur(16px)', boxShadow: '0 40px 100px rgba(0,0,0,0.4)',
                }}>
                    {config.icon}

                    <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em', mb: 2 }}>
                        {config.heading}
                    </Typography>

                    <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '1.05rem', lineHeight: 1.7, mb: 5 }}>
                        {config.body}
                    </Typography>

                    {status === 'success' && (
                        <Box sx={{ p: 3, mb: 5, borderRadius: '16px', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', lineHeight: 1.7 }}>
                                Changed your mind?{' '}
                                <Box component="a" href="/#waitlist" sx={{ color: '#6366f1', textDecoration: 'none', fontWeight: 700, '&:hover': { textDecoration: 'underline' } }}>
                                    Rejoin the waitlist →
                                </Box>
                            </Typography>
                        </Box>
                    )}

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                        <Button
                            href="/"
                            variant="contained"
                            sx={{ borderRadius: '100px', bgcolor: '#6366f1', fontWeight: 700, px: 4, '&:hover': { bgcolor: '#818cf8' } }}
                        >
                            ← Back to Socialixer
                        </Button>
                        {status === 'success' && (
                            <Button
                                href="/#waitlist"
                                variant="outlined"
                                sx={{ borderRadius: '100px', borderColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', fontWeight: 700, px: 4, '&:hover': { borderColor: '#6366f1', color: '#a5b4fc' } }}
                            >
                                Rejoin waitlist
                            </Button>
                        )}
                    </Stack>
                </Box>
            </Container>
        </Box>
    );
}
